import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import _ from 'lodash'
import { useSessionStorage, useLocalStorage } from '~/lib/helpers/storage'
import logger from '~/lib/logger'
import { isDev, isLocal } from '~/util'

const REQUIRED_KEYS = ['name', 'timestamp', 'properties']

const sessionStorage = useSessionStorage()
const localStorage = useLocalStorage()

export const LTStorageKeys = {
  SESSION_ID: 'session_id',
  USER_ID: 'user_id',
  ANONYMOUS_ID: 'anonymous_id',
  LANGUAGE: 'language',
  CURRENCY: 'currency',
  REFERRING_PRODUCT: 'referring_product',
  COUNTRY_CODE: 'country',
}

export const LTEvents = {
  LOGIN: 'login',
  SIGNUP: 'signup',
  LOGOUT: 'logout',
  IDENTIFY: '$identify',
  LOGO_CREATED: 'logo_created',
  LOGO_SELECTED: 'logo_selected',
  PLAN_PURCHASED: 'plan_purchased',
  UPGRADE_MODAL_LAUNCHED: 'upgrade_modal_launched',
  DOMAINS_SEARCHED: 'domains_searched',
  DOMAIN_SELECTED: 'domain_selected',
  DOMAINS_PURCHASED: 'domains_purchased',
  ONE_PAGE_OPENED: 'one_page_opened',
  ONE_PAGE_EDITED: 'one_page_edited',
  ONE_PAGE_PUBLISHED: 'one_page_published',
  ONE_PAGE_CUSTOM_DOMAIN_ADDED: 'one_page_custom_domain_added',
  STITCH_OPENED: 'stitch_opened',
  PURCHASED: 'purchased',
  PAGE_VIEWED: 'page_viewed',
  BNG_SEARCHED: 'bng_searched',
  BNG_FAVOURITED: 'bng_favourited',
  BNG_SELECTED: 'bng_selected',
  CANCEL_PAGE_OPENED: 'cancel_page_opened',
  CANCEL_PAGE_CONFIRMED: 'cancel_page_confirmed',
  PLAN_REACTIVATED: 'plan_reactivated',
  IDEAS_PAGE_OPENED: 'ideas_page_opened',
  LOGO_EDITOR_OPENED: 'logo_editor_opened',
  RESIZER_OPENED: 'resizer_opened',
  RESIZER_DOWNLOADED: 'resizer_downloaded',
  LOGO_FILES_OPENED: 'logo_files_opened',
  LOGO_FILES_DOWNLOADED: 'logo_files_downloaded',
  SIGNATURE_DOWNLOADED: 'signature_downloaded',
  MOCKUPS_OPENED: 'mockups_opened',
  MOCKUPS_DOWNLOADED: 'mockups_downloaded',
  MULTIPAGE_SITE_OPENED: 'multipage_sites_opened',
  MULTIPAGE_SITE_PUBLISHED: 'multipage_sites_published',
  MERCH_OPENED: 'merch_opened',
  MERCH_CLICKED: 'merch_clicked',
  GYB_OPENED: 'gyb_opened',
  GYB_CLICKED: 'gyb_clicked',
  BRAND_GUIDELINES_OPENED: 'brand_guidelines_opened',
  SOCIAL_KIT_OPENED: 'social_kit_opened',
  SOCIAL_KIT_DOWNLOADED: 'social_kit_downloaded',
  BRAND_INFO_OPENED: 'brand_info_opened',
  BRAND_INFO_SAVED: 'brand_info_saved',
}

/**
 * This function detects which browser is running this script.
 * The order of the checks are important since many user agents
 * include key words used in later checks.
 */
function detectBrowser(user_agent, vendor, opera) {
  vendor = vendor || '' // vendor is undefined for at least IE9
  if (opera || _.includes(user_agent, ' OPR/')) {
    if (_.includes(user_agent, 'Mini')) {
      return 'Opera Mini'
    }
    return 'Opera'
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(user_agent)) {
    return 'BlackBerry'
  } else if (
    _.includes(user_agent, 'IEMobile') ||
    _.includes(user_agent, 'WPDesktop')
  ) {
    return 'Internet Explorer Mobile'
  } else if (_.includes(user_agent, 'SamsungBrowser/')) {
    // https://developer.samsung.com/internet/user-agent-string-format
    return 'Samsung Internet'
  } else if (_.includes(user_agent, 'Edge') || _.includes(user_agent, 'Edg/')) {
    return 'Microsoft Edge'
  } else if (_.includes(user_agent, 'FBIOS')) {
    return 'Facebook Mobile'
  } else if (_.includes(user_agent, 'Chrome')) {
    return 'Chrome'
  } else if (_.includes(user_agent, 'CriOS')) {
    return 'Chrome iOS'
  } else if (
    _.includes(user_agent, 'UCWEB') ||
    _.includes(user_agent, 'UCBrowser')
  ) {
    return 'UC Browser'
  } else if (_.includes(user_agent, 'FxiOS')) {
    return 'Firefox iOS'
  } else if (_.includes(vendor, 'Apple')) {
    if (_.includes(user_agent, 'Mobile')) {
      return 'Mobile Safari'
    }
    return 'Safari'
  } else if (_.includes(user_agent, 'Android')) {
    return 'Android Mobile'
  } else if (_.includes(user_agent, 'Konqueror')) {
    return 'Konqueror'
  } else if (_.includes(user_agent, 'Firefox')) {
    return 'Firefox'
  } else if (
    _.includes(user_agent, 'MSIE') ||
    _.includes(user_agent, 'Trident/')
  ) {
    return 'Internet Explorer'
  } else if (_.includes(user_agent, 'Gecko')) {
    return 'Mozilla'
  }
  return ''
}

/**
 * This function detects which browser version is running this script,
 * parsing major and minor version (e.g., 42.1). User agent strings from:
 * http://www.useragentstring.com/pages/useragentstring.php
 */
function detectBrowserVersion(userAgent, vendor, opera) {
  const browser = detectBrowser(userAgent, vendor, opera)
  const versionRegexs = {
    'Internet Explorer Mobile': /rv:(\d+(\.\d+)?)/,
    'Microsoft Edge': /Edge?\/(\d+(\.\d+)?)/,
    Chrome: /Chrome\/(\d+(\.\d+)?)/,
    'Chrome iOS': /CriOS\/(\d+(\.\d+)?)/,
    'UC Browser': /(UCBrowser|UCWEB)\/(\d+(\.\d+)?)/,
    Safari: /Version\/(\d+(\.\d+)?)/,
    'Mobile Safari': /Version\/(\d+(\.\d+)?)/,
    Opera: /(Opera|OPR)\/(\d+(\.\d+)?)/,
    Firefox: /Firefox\/(\d+(\.\d+)?)/,
    'Firefox iOS': /FxiOS\/(\d+(\.\d+)?)/,
    Konqueror: /Konqueror:(\d+(\.\d+)?)/,
    BlackBerry: /BlackBerry (\d+(\.\d+)?)/,
    'Android Mobile': /android\s(\d+(\.\d+)?)/,
    'Samsung Internet': /SamsungBrowser\/(\d+(\.\d+)?)/,
    'Internet Explorer': /(rv:|MSIE )(\d+(\.\d+)?)/,
    Mozilla: /rv:(\d+(\.\d+)?)/,
  }
  const regex = versionRegexs[browser]
  if (regex === undefined) {
    return null
  }
  const matches = userAgent.match(regex)
  if (!matches) {
    return null
  }
  return parseFloat(matches[matches.length - 2])
}

function detectOS(userAgent) {
  const a = userAgent
  if (/Windows/i.test(a)) {
    if (/Phone/.test(a) || /WPDesktop/.test(a)) {
      return 'Windows Phone'
    }
    return 'Windows'
  } else if (/(iPhone|iPad|iPod)/.test(a)) {
    return 'iOS'
  } else if (/Android/.test(a)) {
    return 'Android'
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(a)) {
    return 'BlackBerry'
  } else if (/Mac/i.test(a)) {
    return 'Mac OS X'
  } else if (/Linux/.test(a)) {
    return 'Linux'
  } else if (/CrOS/.test(a)) {
    return 'Chrome OS'
  }
  return ''
}

function detectDevice(user_agent) {
  if (/Windows Phone/i.test(user_agent) || /WPDesktop/.test(user_agent)) {
    return 'Windows Phone'
  } else if (/iPad/.test(user_agent)) {
    return 'iPad'
  } else if (/iPod/.test(user_agent)) {
    return 'iPod Touch'
  } else if (/iPhone/.test(user_agent)) {
    return 'iPhone'
  } else if (/(BlackBerry|PlayBook|BB10)/i.test(user_agent)) {
    return 'BlackBerry'
  } else if (/Android/.test(user_agent)) {
    return 'Android'
  }
  return ''
}

function stripEmptyProperties(obj) {
  const newObj = {}
  for (const key in obj) {
    if (
      obj[key] ||
      typeof obj[key] === 'boolean' ||
      typeof obj[key] === 'number' ||
      typeof obj[key] === 'string'
    ) {
      newObj[key] = obj[key]
    }
  }

  return newObj
}

// Local does not have a Cloudflare worker bound to it, so use dev.
const url =
  isLocal() || isDev()
    ? 'https://dev-events.logo.com/track'
    : 'https://events.logo.com/track'

export const LogoTracker = {
  events: [],
  url,
  session_id: null,
  user_id: null,
  anonymous_id: null,

  identified: false,

  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  enabled: false,
  flush_interval: 3000,

  global_properties: {},

  init() {
    logger.debug('LT: Initializing')
    this.enabled = true

    this.session_id =
      sessionStorage.getItem(LTStorageKeys.SESSION_ID) || uuidv4()
    this.user_id = localStorage.getItem(LTStorageKeys.USER_ID) || null
    this.anonymous_id =
      localStorage.getItem(LTStorageKeys.ANONYMOUS_ID) || uuidv4()

    if (this.user_id) {
      this.identified = true
    }

    sessionStorage.setItem(LTStorageKeys.SESSION_ID, this.session_id)
    localStorage.setItem(LTStorageKeys.ANONYMOUS_ID, this.anonymous_id)

    setInterval(() => this.flushBatch(), this.flush_interval)
  },

  setEnabled(enabled) {
    this.enabled = enabled
  },

  registerGlobalProperties(properties) {
    this.global_properties = {
      ...this.global_properties,
      ...properties,
    }
  },

  identify(user_id) {
    if (this.identified) {
      logger.debug(`LT: Already identified as ${this.user_id}, returning`)
      return
    }

    logger.debug(`LT: Identifying user ${user_id}`)

    this.user_id = user_id
    localStorage.setItem(LTStorageKeys.USER_ID, user_id)

    this.track(LTEvents.IDENTIFY, {
      $anon_id: this.anonymous_id,
      $identified_id: this.user_id,
    })
  },

  reset() {
    logger.debug('LT: Resetting')

    this.events = []
    this.session_id = uuidv4()
    this.anonymous_id = uuidv4()
    this.user_id = null
    this.identified = false
    this.global_properties = {}

    sessionStorage.setItem(LTStorageKeys.SESSION_ID, this.session_id)
    localStorage.removeItem(LTStorageKeys.USER_ID)
    localStorage.setItem(LTStorageKeys.SESSION_ID, this.anonymous_id)
  },

  track(event_name, properties = {}) {
    if (!this.enabled) {
      logger.debug('Tracker disabled, returning.')
      return
    }

    logger.debug(
      `LT: Tracking event ${event_name} - ${JSON.stringify(properties)}`
    )

    if (!event_name) {
      logger.warn('LT: Event name is missing for ', properties)
      return
    }

    try {
      const event = {
        name: event_name,
        session_id: this.session_id,
        user_id: this.user_id,
        anonymous_id: this.anonymous_id,
        timestamp: new Date().toISOString(),
        properties: stripEmptyProperties({
          ...properties,
          ...this.global_properties,
          ...this.deviceProperties(),
        }),
      }

      REQUIRED_KEYS.forEach(key => {
        if (!event[key]) {
          console.error(`Missing required key: ${key}`)
        }
      })

      this.events.push(event)
    } catch (e) {
      console.error(e)
    }
  },

  async flushBatch() {
    logger.debug(`LT: Flushing batch of ${this.events.length} events`)
    if (this.events.length === 0) {
      return
    }

    const { events } = this
    this.events = []

    try {
      await fetch(this.url, {
        method: 'POST',
        body: JSON.stringify({ events }),
      })
    } catch (e) {
      console.error(e)
    }
  },

  deviceProperties() {
    const user_agent = navigator.userAgent

    let path = window.location.pathname
    if (path.endsWith('/')) {
      // Remove trailing slash
      path = path.slice(0, -1)
    }

    return {
      browser: detectBrowser(user_agent, navigator.vendor, window.opera),
      browser_version: detectBrowserVersion(
        user_agent,
        navigator.vendor,
        window.opera
      ),
      os: detectOS(user_agent),
      device: detectDevice(user_agent),
      referrer: document.referrer?.slice(0, 200) || null,

      screen_width: window.screen.width,
      screen_height: window.screen.height,
      user_agent: navigator.userAgent,
      language: Cookies.get('language') || navigator.language,
      country_code: Cookies.get('country') || null,
      currency: Cookies.get('currency') || null,
      timezone: Intl?.DateTimeFormat().resolvedOptions().timeZone,
      path: window.location.pathname,
      hostname: window.location.hostname,
    }
  },
}
